/**
 * `<TotpEnrolmentDisplay>` — shared affordance for showing a freshly
 * issued TOTP secret. Used on both forced-enrolment surfaces:
 *
 *   - `routes/admin/sign-in.tsx` (forced first-login enrolment)
 *   - `routes/admin/user-detail.tsx` (self-rotate)
 *
 * Renders, in order:
 *   1. A "save this secret now" warning banner.
 *   2. A QR code (SVG) of the otpauth URI for camera scans, with a
 *      caption pointing manual-entry users at the secret below.
 *   3. The base32 secret in a readonly input + Copy button.
 *   4. The full otpauth URI (collapsed inside `<details>` by default —
 *      desktop password managers can paste the URI directly, but the
 *      QR is the headline path) with its own Copy button.
 *
 * The QR is rendered client-side from the otpauth URI the API already
 * returns — no new endpoint, no extra secret over the wire. SVG (not
 * canvas/PNG) so the code stays crisp at any zoom and copy-pastes
 * cleanly. `errorCorrectionLevel: 'M'` is plenty for the ~100-char
 * payload at desk-distance scan distance. Foreground/background are
 * pinned to pure black/white regardless of theme — authenticator
 * cameras are uneven about off-colour QRs and the secret is too
 * important for a styling choice to break the scan.
 */

import { useEffect, useState } from 'react';
import * as QRCode from 'qrcode';

interface Props {
  otpauthUri: string;
  secretBase32: string;
  /** Optional override for the warning banner copy. The two surfaces
   *  word it slightly differently — sign-in says "exactly once", the
   *  user-detail rotate path says "hand it off to the admin". */
  warning?: React.ReactNode;
  /** id prefix so multiple instances on a page keep their input/label
   *  associations distinct. Defaults to `totp-enrol`. */
  idPrefix?: string;
}

export function TotpEnrolmentDisplay({
  otpauthUri,
  secretBase32,
  warning,
  idPrefix = 'totp-enrol',
}: Props): JSX.Element {
  const [qrSvg, setQrSvg] = useState<string | null>(null);
  const [copyState, setCopyState] = useState<'secret' | 'uri' | null>(null);

  useEffect(() => {
    let cancelled = false;
    void QRCode.toString(otpauthUri, {
      type: 'svg',
      errorCorrectionLevel: 'M',
      margin: 1,
      color: { dark: '#000000ff', light: '#ffffffff' },
    }).then((svg) => {
      if (!cancelled) setQrSvg(svg);
    });
    return () => {
      cancelled = true;
    };
  }, [otpauthUri]);

  async function copy(target: 'secret' | 'uri'): Promise<void> {
    const value = target === 'secret' ? secretBase32 : otpauthUri;
    try {
      await navigator.clipboard.writeText(value);
      setCopyState(target);
      window.setTimeout(() => setCopyState(null), 2000);
    } catch {
      // leave the inputs visible so the admin can select text manually
    }
  }

  const secretInputId = `${idPrefix}-secret`;
  const uriInputId = `${idPrefix}-uri`;

  return (
    <div className="admin-user-totp-result totp-enrolment-display" role="status">
      <p className="admin-user-totp-warning">
        {warning ?? (
          <>
            <strong>Save this secret now</strong> — it's shown exactly once. Use Google
            Authenticator, 1Password, Authy, or Bitwarden.
          </>
        )}
      </p>

      <div className="totp-enrolment-qr-wrap">
        {qrSvg !== null ? (
          <div
            className="totp-enrolment-qr"
            role="img"
            aria-label="QR code; scan with your authenticator app"
            dangerouslySetInnerHTML={{ __html: qrSvg }}
          />
        ) : (
          <div className="totp-enrolment-qr totp-enrolment-qr-placeholder" aria-hidden="true" />
        )}
        <p className="totp-enrolment-qr-caption">
          Can't scan? Use the secret below for manual entry.
        </p>
      </div>

      <div className="admin-user-totp-field">
        <label htmlFor={secretInputId}>Base32 secret (manual entry)</label>
        <div className="admin-user-totp-field-row">
          <input
            id={secretInputId}
            readOnly
            value={secretBase32}
            onFocus={(e) => e.currentTarget.select()}
          />
          <button type="button" onClick={() => void copy('secret')}>
            {copyState === 'secret' ? 'Copied!' : 'Copy'}
          </button>
        </div>
      </div>

      <details className="totp-enrolment-uri-details">
        <summary>Show otpauth URI (paste into a password manager)</summary>
        <div className="admin-user-totp-field">
          <label htmlFor={uriInputId}>otpauth URI</label>
          <div className="admin-user-totp-field-row">
            <input
              id={uriInputId}
              readOnly
              value={otpauthUri}
              onFocus={(e) => e.currentTarget.select()}
            />
            <button type="button" onClick={() => void copy('uri')}>
              {copyState === 'uri' ? 'Copied!' : 'Copy'}
            </button>
          </div>
        </div>
      </details>
    </div>
  );
}
